import { clamp } from 'motion';
import type { WritableAtom } from 'nanostores';
import { type PropsWithChildren, useState } from 'react';

export function useFloating(props: {
  x: number;
  y: number;
  width: number;
  height: number;
  visible: WritableAtom<boolean>;
}) {
  const [x, setX] = useState(props.x);
  const [y, setY] = useState(props.y);
  const [width, setWidth] = useState(props.width);
  const [height, setHeight] = useState(props.height);

  return {
    x,
    y,
    width,
    height,
    setX,
    setY,
    setWidth,
    setHeight,
    onClose: () => props.visible.set(false),
  };
}

export function StandaloneFloating({
  x: ox,
  y: oy,
  width: owidth,
  height: oheight,
  title,
  onClose,
  children,
}: PropsWithChildren<{
  x: number;
  y: number;
  width: number;
  height: number;
  onClose: () => void;
  title: string;
}>) {
  const [x, setX] = useState(ox);
  const [y, setY] = useState(oy);
  const [width, setWidth] = useState(owidth);
  const [height, setHeight] = useState(oheight);

  return (
    <Floating
      x={x}
      y={y}
      width={width}
      height={height}
      setX={setX}
      setY={setY}
      setWidth={setWidth}
      setHeight={setHeight}
      onClose={onClose}
      title={title}
    >
      {children}
    </Floating>
  );
}

export function Floating({
  x,
  y,
  width,
  height,
  setX,
  setY,
  setWidth,
  setHeight,
  onClose,
  title,
  children,
}: PropsWithChildren<ReturnType<typeof useFloating> & { title: string }>) {
  function onMovePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();

    const pointerTarget = e.currentTarget;
    pointerTarget.setPointerCapture(e.pointerId);

    const ogX = x;
    const ogY = y;
    const { clientX, clientY } = e;

    function onPointerMove(e: PointerEvent) {
      e.preventDefault();
      const dx = e.clientX - clientX;
      const dy = e.clientY - clientY;
      const newX = ogX + dx;
      const newY = ogY + dy;

      const padding = 50;

      const minX = padding - width;
      const minY = 0;
      const maxX = window.innerWidth - padding;
      const maxY = window.innerHeight - padding;

      setX(clamp(minX, maxX, newX));
      setY(clamp(minY, maxY, newY));
    }

    function cleanup() {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointercancel', onPointerCancel);

      if (pointerTarget.hasPointerCapture(e.pointerId)) {
        pointerTarget.releasePointerCapture(e.pointerId);
      }
    }

    function onPointerUp() {
      cleanup();
    }

    function onPointerCancel() {
      cleanup();
    }

    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    document.addEventListener('pointercancel', onPointerCancel);
  }

  function onResizePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();

    const pointerTarget = e.currentTarget;
    pointerTarget.setPointerCapture(e.pointerId);

    const ogWidth = width;
    const ogHeight = height;
    const { clientX, clientY } = e;

    function onPointerMove(e: PointerEvent) {
      e.preventDefault();
      const dx = e.clientX - clientX;
      const dy = e.clientY - clientY;
      const newWidth = ogWidth + dx;
      const newHeight = ogHeight + dy;

      const padding = 50;

      const minWidth = 100;
      const minHeight = 100;
      const maxWidth = window.innerWidth - padding;
      const maxHeight = window.innerHeight - padding;

      setWidth(clamp(minWidth, maxWidth, newWidth));
      setHeight(clamp(minHeight, maxHeight, newHeight));
    }

    function cleanup() {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointercancel', onPointerCancel);

      if (pointerTarget.hasPointerCapture(e.pointerId)) {
        pointerTarget.releasePointerCapture(e.pointerId);
      }
    }

    function onPointerUp() {
      cleanup();
    }

    function onPointerCancel() {
      cleanup();
    }

    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    document.addEventListener('pointercancel', onPointerCancel);
  }

  return (
    <div
      className="fixed z-50 flex flex-col overflow-hidden rounded border border-gray-200 bg-white shadow-md"
      style={{
        left: x,
        top: y,
        width,
        height,
      }}
    >
      <div
        className="flex cursor-move touch-none items-center justify-between border-b border-gray-200 p-1 pl-2 text-sm select-none"
        onPointerDown={onMovePointerDown}
      >
        <h2 className="font-semibold text-gray-800">{title}</h2>
        <button
          onPointerDown={onClose}
          className="cursor-pointer rounded-xs text-sm font-medium text-gray-700 transition hover:bg-red-100"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            className="h-5 w-5 fill-current"
          >
            <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
          </svg>
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">{children}</div>

      <div
        className="absolute right-0 bottom-0 flex cursor-nwse-resize items-center justify-end px-2 py-1 text-sm"
        onPointerDown={onResizePointerDown}
        style={{ touchAction: 'none', userSelect: 'none' }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          className="h-8 w-8"
        >
          <path d="M22,22H20V20H22V22M22,18H20V16H22V18M18,22H16V20H18V22M18,18H16V16H18V18M14,22H12V20H14V22M22,14H20V12H22V14Z" />
        </svg>
      </div>
    </div>
  );
}
